import { EventEmitter, inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { debounceTime, map, startWith, switchMap } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import {
  InspectionAssemblyItem,
  InspectionMeasureType,
  MeasureValues,
} from 'src/app/api/elite/types/inspection-assembly-item';
import { SpecialLabel } from '../../../../api/elite/types/special-label';
import { Certificate } from '../../../../api/elite/types/certificate';
import { QualityControlInspectionDataFormApiService } from '../../api/form.service';

type FormMeasureType = {
  min: FormControl<number>;
  expected: FormControl<number>;
  max: FormControl<number>;
  type: FormControl<string>;
  toolType: FormControl<number>;
  timesToMeasure: FormControl<number>;
  searchType: FormControl<string>;
};

export function inspectionInputsRequired(
  fieldA: string,
  fieldB: string
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const controlA = group.get(fieldA);
    const controlB = group.get(fieldB);

    if (!controlA || !controlB) {
      return null;
    }

    const a = controlA.value;
    const b = controlB.value;

    const hasA = a != null || a > 0;
    const hasB = b != null || b > 0;

    return (hasA && hasB) || (!hasA && !hasB)
      ? null
      : { inspectionInputRequired: true };
  };
}

@Injectable()
export class QualityControlInspectionDataDetailsService {
  private readonly apiService = inject(
    QualityControlInspectionDataFormApiService
  );

  private readonly fb = inject(FormBuilder);

  private _aqlTypes: { id: number; name: string }[] = [];

  // get aqlTypes(): { id: number; name: string }[] {
  //   return [...this._aqlTypes];
  // }

  private _eeChangeForMeasure = new EventEmitter<void>();

  private _measureTypes = new BehaviorSubject<InspectionMeasureType[]>([]);
  private _specialLabels = new BehaviorSubject<SpecialLabel[]>([]);

  dialogData: InspectionAssemblyItem | undefined = undefined;
  enableQtyInspectionInputs: boolean | undefined = undefined;

  measureTypesSelectableData$ = this._eeChangeForMeasure.asObservable().pipe(
    startWith(0),
    switchMap(() => {
      return combineLatest([
        this._measureTypes.asObservable(),
        this.measuresFormSelected?.valueChanges
          ? this.measuresFormSelected?.valueChanges.pipe(
              startWith(this.measuresFormSelected.value)
            )
          : of([]),
      ]);
    }),
    map(([res, formArray]) => {
      const selectable = res.filter(
        (type) => !formArray.find((val) => val.type === type.title)
      );
      return { selectable, list: res };
    })
  );

  private _inspectionTests = new BehaviorSubject<
    { id: number; title: string }[]
  >([]);
  inspectionTests$ = this._inspectionTests.asObservable();

  private _certificates = new BehaviorSubject<Certificate[]>([]);
  certificates$ = this._certificates.asObservable();

  private urlRegex =
    '^(http(s?):\\/\\/.)[-a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,6}\\b([-a-zA-Z0-9@:%_+.~#?&//=]*)$';
  mainForm = new FormGroup(
    {
      assemblyName: new FormControl(undefined, [Validators.required]),
      urlDrive: new FormControl(undefined, [
        Validators.required,
        Validators.pattern(this.urlRegex),
      ]),
      numberOfGroups: new FormControl<number>(1),
      hasMeasureInspection: new FormControl(false),
      hasVisualInspection: new FormControl(false),
      hasCobotInspection: new FormControl(false),
      searchAqlType: new FormControl<string>(undefined),
      aqlType: new FormControl<number>(undefined),
      specialLabelId: new FormControl<number>(undefined),
      searchLabel: new FormControl<string>(undefined),
      quantityPerInspection: new FormControl<number>(undefined),
      quantityToInspect: new FormControl<number>(undefined),
    },
    {
      validators: inspectionInputsRequired(
        'quantityPerInspection',
        'quantityToInspect'
      ),
    }
  );

  selectedGroupControl = new FormControl(0);
  optionsGroup$ = this.mainForm.controls.numberOfGroups.valueChanges.pipe(
    startWith(1),
    map((val) => {
      if (!val || val < 1) {
        return [];
      }
      return Array.from({ length: val }, (_, i) => 'Grupo ' + (i + 1));
    })
  );

  optionsSpecialLabel$ = combineLatest([
    this._specialLabels,
    this.mainForm
      .get('searchLabel')
      .valueChanges.pipe(startWith(''), debounceTime(300)),
  ]).pipe(
    map(([items, search]) => {
      return search
        ? items.filter((item) =>
            item.title.toUpperCase().includes(search.toUpperCase())
          )
        : items;
    })
  );

  private measuresForm: FormArray<FormArray<FormGroup<FormMeasureType>>> =
    this.fb.array([this.fb.array<FormGroup<FormMeasureType>>([])]);
  measuresFormSelected$ = this.selectedGroupControl.valueChanges.pipe(
    startWith(0),
    switchMap((val = 0) => {
      return of(this.measuresForm.at(val || 0));
    })
  );
  // leftOptionsToSelect$ = this.measuresFormSelected$.pipe(
  //   switchMap((formArray) => {
  //     return this._measureTypes
  //       .asObservable()
  //       .pipe(
  //         map((types) =>
  //           types.filter(
  //             (ty) => !formArray.value.find((form) => form.type === ty.title)
  //           )
  //         )
  //       );
  //   })
  // );
  leftOptionsToSelect$ = this.measureTypesSelectableData$.pipe(
    map((data) => data?.selectable?.length ?? 0)
  );

  aqlTypesFiltered$ = this.mainForm.controls.searchAqlType.valueChanges.pipe(
    startWith(''),
    map((val = '') => {
      return this._aqlTypes.filter((el) =>
        el.name.toLowerCase().includes(val.toLowerCase())
      );
    })
  );

  private get measuresFormSelected(): FormArray<FormGroup<FormMeasureType>> {
    return this.measuresForm.at(this.selectedGroupControl.value) as FormArray;
  }

  constructor() {
    this.apiService
      .data()
      .subscribe(
        ({
          measureTypes,
          testTypes,
          aqlTypes,
          specialLabels,
          certificates,
        }) => {
          this._measureTypes.next(measureTypes);
          this._inspectionTests.next(testTypes);
          this._aqlTypes = aqlTypes;
          this._specialLabels.next(specialLabels);
          this._certificates.next(certificates);
          this.initForm(this.dialogData);

          if (!this.dialogData) {
            this.mainForm
              .get('aqlType')
              .setValue(this._aqlTypes.find((el) => el.name === '2.5')?.id);
          }
          this.mainForm.controls.searchAqlType.setValue(undefined);
        }
      );

    this.mainForm.controls.numberOfGroups.valueChanges.subscribe((val) => {
      if (!val) {
        return;
      }
      const firstValue = this._measureTypes.value[0];
      const groupArray: FormArray[] = [];
      for (let i = 0; i < val; i++) {
        const measures = this.fb.array([]);
        this.addMeasureForm(firstValue, measures);
        groupArray.push(measures);
      }
      this.measuresForm = this.fb.array(groupArray);
      this.selectedGroupControl.setValue(0);
    });
  }

  initForm(data?: InspectionAssemblyItem): void {
    if (data !== undefined) {
      this.mainForm.controls.assemblyName.setValue(data.assemblyName);
      this.mainForm.controls.urlDrive.setValue(data.urlDrive);
      this.mainForm.controls.specialLabelId.setValue(
        data.specialLabelId > 0 ? data.specialLabelId : 0
      );
      const groups = Array.from(
        new Set(data.measureValues.map((el) => el?.group))
      );
      this.mainForm.controls.numberOfGroups.setValue(groups?.length || 1);
      this.measuresForm = this.fb.array(
        groups.map(() => this.fb.array<FormGroup<FormMeasureType>>([]))
      );
      data.measureValues?.forEach((val) => {
        const newMeasure = this.createMeasureForm(val);
        if (!val?.group) {
          this.measuresForm.at(0).push(newMeasure);
        } else {
          this.measuresForm.at(val.group - 1).push(newMeasure);
        }
      });
      this.mainForm.get('aqlType').setValue(data.aqlTypeId);
      this.mainForm.get('hasCobotInspection').setValue(data.cobotInspection ?? false);
      if (this.enableQtyInspectionInputs) {
        this.mainForm
          .get('quantityPerInspection')
          .setValue(data.quantityPerInspection);
        this.mainForm.get('quantityToInspect').setValue(data.quantityToInspect);
        this.mainForm.addValidators(
          inspectionInputsRequired('quantityPerInspection', 'quantityToInspect')
        );
      }
    } else {
      this.desactiveForm();
    }

    this.selectedGroupControl.setValue(0);
    this.mainForm.get('searchAqlType').setValue(undefined);
    this.mainForm.get('hasVisualInspection').valueChanges.subscribe((val) => {
      if (val) {
        this.mainForm.get('aqlType').setValidators(Validators.required);
        this.mainForm
          .get('aqlType')
          .setValue(this._aqlTypes.find((el) => el.name === '2.5')?.id);
      } else {
        this.mainForm.get('aqlType').setValue(undefined);
        this.mainForm.get('aqlType').clearValidators();
        this.mainForm.get('aqlType').updateValueAndValidity();
        this.mainForm.get('searchAqlType').setValue(undefined);
        // cobot requires visual inspection to be active
        this.mainForm.get('hasCobotInspection').setValue(false);
      }
    });
    this.mainForm.get('hasMeasureInspection').valueChanges.subscribe((val) => {
      if (val) {
        this.mainForm
          .get('urlDrive')
          .setValidators([
            Validators.required,
            Validators.pattern(this.urlRegex),
          ]);
        if (data) {
          this.mainForm.get('urlDrive').setValue(data?.urlDrive);
        }
      } else {
        this.mainForm.get('urlDrive').clearValidators();
        this.mainForm.get('urlDrive').updateValueAndValidity();
        this.mainForm.get('urlDrive').setValue(undefined);
      }
    });
  }

  desactiveForm(): void {
    this.mainForm.get('urlDrive').clearValidators();
    this.mainForm.get('urlDrive').updateValueAndValidity();
    this.mainForm.get('urlDrive').setValue(undefined);
  }

  setAssemblyName(itemName: string): void {
    if (!itemName) {
      return;
    }
    this.mainForm.controls.assemblyName.setValue(itemName);
    this.mainForm.controls.assemblyName.disable();
  }

  disableForm(): void {
    this.mainForm.disable();
    this.measuresForm.disable();
  }

  addMeasureForm(
    firstVal?: InspectionMeasureType,
    formToPush?: FormArray
  ): void {
    if (!firstVal) {
      firstVal = this.selectableMeasuresTypes()[0];
    }
    const measure = this.createMeasureForm({
      measureType: firstVal,
      min: undefined,
      expected: undefined,
      max: undefined,
      measureToolId: undefined,
    });
    if (formToPush) {
      formToPush.push(measure);
    } else {
      this.measuresFormSelected.push(measure);
    }
    this._eeChangeForMeasure.emit();
  }

  private createMeasureForm(item?: MeasureValues): FormGroup<FormMeasureType> {
    return this.fb.group({
      min: [item.min, [Validators.required]],
      expected: [item.expected, [Validators.required]],
      max: [item.max, [Validators.required]],
      type: [item?.measureType?.title, [Validators.required]],
      toolType: [item.measureToolId],
      timesToMeasure: [item.timesToMeasure],
      searchType: [''],
    });
  }

  protected selectableMeasuresTypes(): InspectionMeasureType[] {
    return this._measureTypes.value.filter(
      (type) =>
        !this.measuresFormSelected.value.find((val) => val.type === type.title)
    );
  }

  removeMeasure(index: number): void {
    const quantity = this.measuresForm
      .getRawValue()
      .reduce((val, el) => el.length + val, 0);
    if (quantity <= 1) {
      return;
    }
    this.measuresFormSelected.removeAt(index);
    this._eeChangeForMeasure.emit();
  }

  onChangeMeasureType(): void {
    this._eeChangeForMeasure.emit();
  }

  getMeasuresData(): {
    min: number;
    expected: number;
    max: number;
    type: string;
    group: number;
    measureToolId?: number;
  }[] {
    return this.measuresForm?.controls
      .map((group, index) => {
        return group?.controls?.map((measure) => {
          return {
            ...measure?.getRawValue(),
            toolType: undefined,
            measureToolId: measure.get('toolType')?.value,
            group: index + 1,
          };
        });
      })
      .flat();
  }
}
